import React, { useState, useEffect, useRef } from "react";
import { css } from "@emotion/react";
import { fromUnixTime, isSameDay, format } from "date-fns";
import { CometChat } from "@cometchat-pro/chat";
import { UserMetaData } from "components/chat/types";
import BubbleText from "components/chat/shared/BubbleText";
import BubbleImage from "components/chat/shared/BubbleImage";
import BubbleFile from "components/chat/shared/BubbleFile";
import BubbleDeleted from "components/chat/shared/BubbleDeleted";

// mui
import Stack from "@mui/material/Stack";

const isMyOrgMsg = (user: CometChat.User | null, msg: CometChat.BaseMessage) => {
  if (user === null) {
    return false;
  }
  const myMetadata = user.getMetadata() as unknown as UserMetaData;
  const msgSenderMetaData = msg.getSender().getMetadata() as unknown as UserMetaData;
  if (myMetadata.org.kind === "tenant") {
    return msgSenderMetaData.org.kind === "tenant";
  } else {
    return msgSenderMetaData.org.kind === "supplier_firm";
  }
};

const isMyMsg = (user: CometChat.User | null, msg: CometChat.BaseMessage) => {
  if (user === null) {
    return false;
  }
  return user.getUid() === msg.getSender().getUid();
};

const style = {
  dateSeparator: css`
    padding: 8px 12px;
    background-color: #f6f6f6;
    border-radius: 10px;
  `,
};

type MessageListProps = {
  msgs: CometChat.BaseMessage[];
  handleMsgEdit: (textMsg: CometChat.TextMessage) => void;
  handleMsgDelete: (msg: CometChat.BaseMessage) => void;
};
const MessageList: React.FC<MessageListProps> = (props) => {
  const { msgs, handleMsgEdit, handleMsgDelete } = props;
  const [user, setUser] = useState<CometChat.User | null>(null);

  const lastDummyMsgRef = useRef<HTMLDivElement>(null); // 一番下までscrollするのに使う
  const scrollToBottom = () => {
    lastDummyMsgRef.current?.scrollIntoView();
  };

  useEffect(() => {
    CometChat.getLoggedinUser().then((cometChatUser) => setUser(cometChatUser));
  }, []);

  // 一番最後のメッセージの変化に対してスクロールを一番下までもっていくことで
  // - 最初に描画したとき
  // - 新しいメッセージを受信したとき
  // - 新しいメッセージを送信したとき
  // の3つに対応できる
  useEffect(() => {
    scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgs[msgs.length - 1]]);

  const getListItemComponent = (msg: CometChat.BaseMessage) => {
    // https://www.cometchat.com/docs/javascript-chat-sdk/message-structure-and-hierarchy
    // 必要に応じて追加 対応してないものはnullを返してるので表示されない
    // CometChat.CATEGORY_ACTION:
    // CometChat.CATEGORY_CALL
    // CometChat.CATEGORY_MESSAGE
    // CometChat.CATEGORY_CUSTOM
    switch (msg.getCategory()) {
      case CometChat.CATEGORY_MESSAGE:
        const myOrg = isMyOrgMsg(user, msg);
        const myMsg = isMyMsg(user, msg);
        if (msg.getDeletedAt()) {
          return <BubbleDeleted msg={msg} position={myOrg ? "right" : "left"} />;
        }
        switch (msg.getType()) {
          case CometChat.MESSAGE_TYPE.TEXT:
            return (
              <BubbleText
                msg={msg as CometChat.TextMessage}
                position={myOrg ? "right" : "left"}
                isMyMsg={myMsg}
                handleMsgEdit={handleMsgEdit}
                handleMsgDelete={handleMsgDelete}
              />
            );
          case CometChat.MESSAGE_TYPE.IMAGE:
            return (
              <BubbleImage
                msg={msg as CometChat.MediaMessage}
                position={myOrg ? "right" : "left"}
                isMyMsg={myMsg}
                handleMsgDelete={handleMsgDelete}
              />
            );
          case CometChat.MESSAGE_TYPE.FILE:
            return (
              <BubbleFile
                msg={msg as CometChat.MediaMessage}
                position={myOrg ? "right" : "left"}
                isMyMsg={myMsg}
                handleMsgDelete={handleMsgDelete}
              />
            );
          default:
            return null;
        }
      default:
        return null;
    }
  };

  // 日付が変わったら日付を表示する
  const dateSeparator = (currentMsg: CometChat.BaseMessage, prevMsg: CometChat.BaseMessage | undefined) => {
    const currentMsgUtc = fromUnixTime(currentMsg.getSentAt());
    const content = (
      <Stack alignItems="center">
        <span css={style.dateSeparator}>{format(currentMsgUtc, "yyyy-MM-dd")}</span>
      </Stack>
    );

    if (prevMsg == undefined) {
      return content;
    }

    const prevMsgUtc = fromUnixTime(prevMsg.getSentAt());
    if (isSameDay(currentMsgUtc, prevMsgUtc)) {
      return null;
    }

    return content;
  };

  return (
    <>
      {msgs.map((msg: CometChat.BaseMessage, idx: number) => (
        <div key={idx}>
          {dateSeparator(msg, msgs[idx - 1])}
          {getListItemComponent(msg)}
        </div>
      ))}
      <div ref={lastDummyMsgRef} />
    </>
  );
};

export default MessageList;
